import { Alert, Card, Table, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { EmptyState } from '@/shared/ui/EmptyState'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { PermissionNotice } from '@/shared/ui/PermissionNotice'
import { fetchPayslips, isKnownOrigin, type PayrollLine, type PayrollLineItem } from './payrollApi'

interface Props {
  personId: string
}

/**
 * Hisob-kitob varaqalari — xodimning vedomostdagi satrlari.
 *
 * <h2>⚠️ BU VEDOMOSTDAN BOSHQA ENDPOINT VA BOSHQA DOIRA</h2>
 * `GET /payroll/persons/{id}/payslips` `HrScope.PERSON` ni ishlatadi
 * (u yerda `OWN` BOR), vedomost ro'yxati esa `HrScope.PAYROLL_RUN` ni
 * (u yerda `OWN` ATAYLAB yo'q). Ya'ni xodim o'z varaqasini ko'ra
 * oladi, lekin butun filialning vedomostini emas. Ikkalasini «bir xil
 * ma'lumot» deb birlashtirish shu farqni yo'q qilardi.
 *
 * <h2>⚠️ FAQAT TASDIQLANGAN VA TO'LANGAN</h2>
 * LOYIHA holatidagi son hali o'zgaradi (qayta yig'ish mumkin). Uni
 * varaqada ko'rsatish «maoshim kamayib ketdi» degan savolni yaratardi.
 *
 * <h2>⚠️ SATRDAGI QISM NOMI MUZLATILGAN</h2>
 * `componentCode` yig'ilgan paytdagi nusxa. Katalogda nom keyin
 * o'zgarsa ham eski varaq o'sha-o'sha qoladi — «o'tgan oyda nima uchun
 * to'landi» degan savol javobsiz qolmasin.
 */
export function PayslipsCard({ personId }: Props) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canView = can('hr.payroll.view')

  const payslips = useQuery({
    queryKey: queryKeys.hr.payslips(personId),
    queryFn: () => fetchPayslips(personId),
    enabled: canView && personId !== '',
  })

  if (!canView) {
    return (
      <Card title={t('hr.payslip.title')}>
        <PermissionNotice
          permission="hr.payroll.view"
          title={t('hr.payslip.hiddenTitle')}
          reason={t('hr.payslip.hiddenReason')}
        />
      </Card>
    )
  }

  const rows = payslips.data ?? []

  const columns: ColumnsType<PayrollLine> = [
    {
      title: t('hr.payroll.orgNode'),
      key: 'orgNode',
      render: (_value, row) => row.orgNodeName ?? '—',
    },
    {
      title: t('hr.payroll.jobTitle'),
      key: 'jobTitle',
      render: (_value, row) => row.jobTitleName ?? '—',
    },
    {
      title: t('hr.payroll.earning'),
      key: 'earning',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.earning),
    },
    {
      title: t('hr.payroll.deduction'),
      key: 'deduction',
      align: 'right',
      render: (_value, row) => formatMoneyValue(row.deduction),
    },
    {
      title: t('hr.payroll.net'),
      key: 'net',
      align: 'right',
      render: (_value, row) => (
        <Typography.Text strong>{formatMoneyValue(row.net)}</Typography.Text>
      ),
    },
    {
      title: t('hr.payroll.lineNote'),
      key: 'note',
      render: (_value, row) => (row.note === null ? '—' : <ExpandableHint text={row.note} />),
    },
  ]

  // ⚠️ Qism satrida VALYUTA YO'Q — `PayrollLineItemView` uni SAQLAMAYDI
  // va bu ataylab: u ota-satrdan (`PayrollLineView.currency`) keladi.
  // Shuning uchun ustunlar o'sha satrning valyutasi bilan quriladi;
  // `getCurrencyCode()` ga tayanish tenant valyutasi keyin
  // o'zgartirilganda ESKI varaqni yangi belgi bilan chizardi.
  const itemColumns = (currency: string): ColumnsType<PayrollLineItem> => [
    {
      title: t('hr.salary.component'),
      key: 'component',
      render: (_value, row) =>
        row.componentCode ? t(`seed.payComponents.${row.componentCode}`, { defaultValue: row.componentCode }) : '—',
    },
    {
      title: t('hr.salary.kind'),
      key: 'kind',
      render: (_value, row) => (
        <Tag color={row.kind === 'EARNING' ? 'green' : 'volcano'}>
          {t(`payComponentKind.${row.kind}`)}
        </Tag>
      ),
    },
    {
      title: t('hr.payroll.origin'),
      key: 'origin',
      // Panel bilmagan yangi manba xom enum bo'lib chiqmasin — tire
      render: (_value, row) =>
        isKnownOrigin(row.origin) ? t(`payOrigin.${row.origin}`) : t('common.dash'),
    },
    {
      title: t('hr.salary.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, row) => formatMoneyValue(money(row.amountMinor, currency)),
    },
    {
      title: t('hr.payEvent.noteField'),
      key: 'note',
      render: (_value, row) => (row.note === null ? '—' : row.note),
    },
  ]

  return (
    <Card title={t('hr.payslip.title')}>
      {payslips.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(payslips.error, t)}
        />
      ) : null}

      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('hr.payslip.hint')}
      </Typography.Paragraph>

      {/* ⚠️ Bo'sh xulosa FAQAT `isSuccess` da (`guards.mjs` 3-bo'limi) */}
      {payslips.isSuccess && rows.length === 0 ? (
        <EmptyState title={t('hr.payslip.empty')} description={t('hr.payslip.emptyHint')} />
      ) : (
        <Table<PayrollLine>
          rowKey={(row) => row.id}
          columns={columns}
          dataSource={[...rows]}
          loading={payslips.isPending}
          pagination={false}
          size="small"
          scroll={{ x: 760 }}
          expandable={{
            expandedRowRender: (row) => (
              <Table<PayrollLineItem>
                rowKey={(item) => item.id}
                columns={itemColumns(row.net.currency)}
                dataSource={[...row.items]}
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      )}
    </Card>
  )
}
